// Equipment warnings describe the work being done now, including changing site
// preparation phases. They never infer a foreign villager's hidden assignment.
import { isTerraform } from '../../shared/constants'
import { buildingMaxHp } from '../../shared/townhall'
import type { ItemType, UnitWire } from '../../shared/types'
import type { GameState } from '../state'
import { sitePrepPhase } from './terrain'

/** The tool this worker is missing, or null when their current work is equipped
 *  correctly or legitimately needs no tool (hand crafting, planting, hauling). */
export function missingWorkTool(st: GameState, u: UnitWire): ItemType | null {
  if (u.type !== 'citizen' || !u.person || u.hp <= 0 || (u.owner !== st.me && !st.observer)) return null
  if (u.state !== 'working' && u.state !== 'building' && u.state !== 'toJob') return null
  if (u.hauler || u.supply !== undefined || u.craftHaul || u.errand || u.exploring || u.scav) return null
  if (u.carrying && Object.values(u.carrying).some(amount => (amount ?? 0) > 0)) return null
  const job = u.job !== undefined ? st.buildings.get(u.job) : undefined
  if (!job || job.owner !== u.owner || job.hp <= 0 || job.paused || job.type === 'plant') return null

  const held = u.person.held
  let tool: ItemType | null = null
  if (!job.done) {
    // Tree clearing also precedes road and terraform work. Only after the
    // footprint is clear can a shovel level it or a hammer raise the building.
    const prep = sitePrepPhase(st, job)
    if (job.type === 'chop' || prep === 'clear') tool = 'axe'
    else if (prep === 'level' || isTerraform(job.type)) tool = 'shovel'
    else tool = 'hammer'
  } else if (u.state === 'building') {
    tool = 'hammer' // repairs use the builder's tool, never the production tool
  } else {
    // Arrival switches hammer-holders at damaged workplaces into repair work.
    // Until then, a walking repairer must not be labelled as an untooled farmer.
    if (u.state === 'toJob' && held === 'hammer' && job.hp < buildingMaxHp(job)) return null
    switch (job.type) {
      case 'farm': tool = 'hoe'; break
      case 'pier': tool = 'rod'; break
      case 'quarry':
      case 'mine': tool = 'pick'; break
      case 'barracks':
        if (job.queue[0]?.item && job.queue[0].ticksLeft > 0) tool = 'smithhammer'
        break
      // Hall/Workbench wooden crafting works at full pace with bare hands.
      // Other finished buildings have no tool-based production job.
      default: break
    }
  }
  return tool !== held ? tool : null
}
